// Types mirroring the NestJS `SeoReport` payload (packages/nestjs/src/seo-report/types.ts).

export type PageGroup = 'Home' | 'Direct Cremation' | 'Locations' | 'Articles' | 'Obituaries';

export type PageKind = 'home' | 'parent' | 'child';

/** Prior-month-to-date scalars (prior month clipped to the same day-of-month). */
export interface PmtdPage {
    impr: number;
    clicks: number;
    fills: number;
    starts: number;
    phone: number;
    pos: number;
    sessions: number;
}

export interface PmtdQuery {
    impr: number;
    clicks: number;
    pos: number;
}

export interface PageRow {
    label: string;
    kind: PageKind;
    impr: number[];
    clicks: number[];
    fills: number[];
    starts: number[];
    phone: number[];
    pos: number[];
    /** Per-month non-paid sessions (visits) landing on the page. */
    sessions: number[];
    /** `<lastmod>` ISO date from the sitemap, or null. Drives content-freshness age. */
    lastmod: string | null;
    /** Prior-month-to-date baseline; present only when the report has a partial month. */
    pmtd?: PmtdPage;
}

export interface GroupData {
    pages: PageRow[];
}

export interface QueryRow {
    q: string;
    theme: string;
    page: string;
    impr: number[];
    clicks: number[];
    pos: number[];
    /** True when this query is also covered by a paid Google Ads search term. */
    paid?: boolean;
    /** Trailing-90d paid spend (dollars) on the matching paid term, when covered. */
    paidCost?: number;
    /** Prior-month-to-date baseline; present only when the report has a partial month. */
    pmtd?: PmtdQuery;
}

/** Site-wide paid-search (Google Ads) coverage summary. */
export interface AdsSummary {
    configured: boolean;
    totalCost90d: number;
    coveredTerms: number;
}

export interface QueryBucket {
    name: 'High-intent' | 'Informational';
    rows: QueryRow[];
    nqueries: number;
    dropped: number;
    subtotal: { impr: number[]; clicks: number[]; pos: number[]; pmtd?: PmtdQuery };
}

/**
 * Non-paid inbound calls received per month. A DIFFERENT metric from `phone`
 * (taps on a tel: link): it includes callers who got the number from Google
 * Business Profile, a saved contact, or an earlier call. Shown on its own line —
 * never as a stand-in for phone clicks.
 */
export interface CallsSeries {
    calls: number[];
    answered: number[];
    pmtd?: { calls: number; answered: number };
}

/**
 * Earliest month each first-party series is real. Anything before renders "—".
 *
 * This exists because the report was badly misread: "not measured" and "measured
 * zero" looked identical, so a fills column that read zero for seven straight
 * months — while the business took 27-57 submissions a month — was taken at face
 * value. A blank says "we do not know"; a zero says "it did not happen".
 */
export interface SeoCoverage {
    fills: string | null;
    /** Later than `fills`: group attribution needs a landing page, which older rows lack. */
    fillsByGroup: string | null;
    phone: string | null;
    calls: string | null;
}

export interface SeoReport {
    months: string[];
    groupOrder: PageGroup[];
    groups: Record<PageGroup, GroupData>;
    /** Site-wide totals across every page (incl. untracked), as an "All pages" row. */
    totals: PageRow;
    mtdMonth: string | null;
    queries: QueryBucket[];
    /** Paid-search (Google Ads) coverage summary; configured:false when Ads is off. */
    ads: AdsSummary;
    /** Non-paid inbound calls received. Separate metric from `phone` clicks. */
    calls?: CallsSeries;
    meta: {
        generatedOn: string;
        startDate: string;
        endDate: string;
        rangeLabel: string;
        warning?: string | null;
        /** Label of the prior-month-to-date window used for the partial month's MoM. */
        priorMtdLabel?: string | null;
        /** From when each first-party conversion series is real; see SeoCoverage. */
        coverage?: SeoCoverage;
    };
}

/** Metric keys shared across the summary / page-group tables. */
export type MetricKey = 'impr' | 'clicks' | 'ctr' | 'position' | 'sessions' | 'starts' | 'fills' | 'phone';

export const METRIC_COLUMNS: { key: MetricKey; label: string }[] = [
    { key: 'impr', label: 'Impressions' },
    { key: 'clicks', label: 'Clicks' },
    { key: 'ctr', label: 'CTR' },
    { key: 'position', label: 'Avg pos' },
    { key: 'sessions', label: 'Visits' },
    { key: 'starts', label: 'Form starts' },
    { key: 'fills', label: 'Form fills' },
    { key: 'phone', label: 'Phone clicks' },
];

/**
 * Was this metric actually being recorded in this month?
 *
 * Only `fills` and `phone` can be unmeasured — they are the two series that moved
 * to first-party data and therefore have a start date. `scope` matters because a
 * fill can be counted site-wide long before it can be credited to a page group:
 * group attribution needs a landing page, and rows predating that capture have
 * none.
 *
 * Returns true for snapshots cached before `coverage` existed, so an old payload
 * renders exactly as it used to rather than blanking every conversion cell.
 */
export function isMeasured(
    report: SeoReport,
    key: MetricKey,
    monthIndex: number,
    scope: 'group' | 'total',
): boolean {
    if (key !== 'fills' && key !== 'phone') return true;

    const cov = report.meta.coverage;
    if (!cov) return true;

    const month = report.months[monthIndex];
    if (!month) return true;

    const from = key === 'fills' ? (scope === 'group' ? cov.fillsByGroup : cov.fills) : cov.phone;
    if (!from) return false; // series holds no data at all

    return month >= from; // "YYYY-MM" sorts lexicographically
}
